export function formatUsd(amount) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '$0.00';
  return `$${numeric.toFixed(2)}`;
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function firstNumber(...values) {
  for (const value of values) {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) return numeric;
  }
  return null;
}

function firstObject(...values) {
  return values.find((value) => value && typeof value === 'object' && !Array.isArray(value)) || null;
}

function normalizeResolution(value) {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
}

function getEstimateRules(modelSpec) {
  return firstObject(
    modelSpec?.estimatedCostRules,
    modelSpec?.estimated_cost_rules,
    modelSpec?.estimatedCostConfig,
    modelSpec?.estimated_cost_config
  );
}

function getResolutionRate(rates, resolution, defaultResolution) {
  const normalizedRates = asObject(rates);
  const selected = normalizeResolution(resolution);
  const fallback = normalizeResolution(defaultResolution);
  if (selected && normalizedRates[selected]) return normalizedRates[selected];
  if (fallback && normalizedRates[fallback]) return normalizedRates[fallback];
  if (normalizedRates['720p']) return normalizedRates['720p'];
  return Object.values(normalizedRates).find((value) => value && typeof value === 'object') || null;
}

function estimateFromRules({ modelSpec, rules, durationSeconds, resolution, inputVideoSeconds }) {
  const type = rules?.type || rules?.pricingType || rules?.pricing_type;
  if (type !== 'byteplus_seedance2') return null;

  const seconds = Number(durationSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  const rates = rules.ratesUsdPerSecond || rules.rates_usd_per_second;
  const rate = getResolutionRate(
    rates,
    resolution,
    rules.defaultResolution || rules.default_resolution || modelSpec?.defaultResolution || modelSpec?.default_resolution
  );
  if (!rate) return null;

  const outputPerSecond = firstNumber(
    rate.output,
    rate.outputUsdPerSecond,
    rate.output_usd_per_second,
    rate.outputCostPerSecondUsd,
    rate.output_cost_per_second_usd
  );
  if (!outputPerSecond) return null;

  const videoInputPerSecond = firstNumber(
    rate.videoInput,
    rate.video_input,
    rate.videoInputUsdPerSecond,
    rate.video_input_usd_per_second
  ) || 0;
  const inputSeconds = Number(inputVideoSeconds);
  const safeInputSeconds = Number.isFinite(inputSeconds) && inputSeconds > 0 ? inputSeconds : 0;

  return (outputPerSecond * seconds) + (videoInputPerSecond * safeInputSeconds);
}

export function estimateVideoCostUsd({
  modelSpec,
  durationSeconds,
  resolution,
  inputVideoSeconds = 0,
}) {
  const rules = getEstimateRules(modelSpec);
  if (rules) {
    const ruleEstimate = estimateFromRules({
      modelSpec,
      rules,
      durationSeconds,
      resolution,
      inputVideoSeconds,
    });
    if (ruleEstimate !== null) return ruleEstimate;
  }

  const perSecond = Number(
    modelSpec?.estimatedCostPerSecondUsd
    ?? modelSpec?.estimated_cost_per_second_usd
    ?? modelSpec?.estimatedCostPerSecond
    ?? modelSpec?.costPerSecondUsd
    ?? modelSpec?.cost_per_second_usd
  );
  const seconds = Number(durationSeconds);

  if (!Number.isFinite(perSecond) || perSecond <= 0) return null;
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  return perSecond * seconds;
}
